import { useLayoutEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";

import { capitalizeFirstLetter } from "../../services/utils";
import {
  AXIS_TICK_FONT_SIZE,
  formatLeadTime,
  LEAD_TIME_BADGE_HEIGHT,
} from "./constants";
import { CustomizedInjectedProps } from "./rechartsCustomized";

const PADDING_X = 4;
const RADIUS = 3;
const LINE_HEIGHT = AXIS_TICK_FONT_SIZE + 2;
// Clearance between the badge and the bottom edge of the drawing.
const BOTTOM_GAP = 2;
const BADGE_HEIGHT = LEAD_TIME_BADGE_HEIGHT - BOTTOM_GAP;
// Fallback width until the text is measured (about 10 chars of a 10px font).
const ESTIMATED_WIDTH = 60;

// Occupation date minus the lead time, as the legacy cockpit shows it under
// the axis ("Mar. 15/09").
export const formatLeadTimeDate = (
  date: string,
  leadTime: number,
  locale: string
): string => {
  const day = DateTime.fromISO(date, { zone: "utc" })
    .plus({ days: leadTime })
    .setLocale(locale);
  return day.isValid ? capitalizeFirstLetter(day.toFormat("ccc dd/MM")) : "";
};

/**
 * Legacy `tooltip-date`: a badge following the hover cursor in the band
 * reserved under the X axis (LEAD_TIME_BADGE_HEIGHT), reading the lead time
 * and the matching calendar date. Rendered through recharts <Customized> so it
 * also follows the cursor synced from the other lead-time chart.
 */
const LeadTimeAxisBadge = ({
  date,
  isTooltipActive,
  activeCoordinate,
  activeLabel,
  offset,
}: CustomizedInjectedProps & { date: string }) => {
  const intl = useIntl();
  const theme = useTheme();
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(ESTIMATED_WIDTH);

  const leadTime = typeof activeLabel === "number" ? activeLabel : null;
  const leadTimeLabel = leadTime != null ? formatLeadTime(intl, leadTime) : "";
  const dateLabel =
    leadTime != null ? formatLeadTimeDate(date, leadTime, intl.locale) : "";

  // Measured after paint: SVG text has no intrinsic box before it is drawn.
  useLayoutEffect(() => {
    const width = textRef.current?.getBBox().width;
    if (width) setTextWidth(width);
  }, [leadTimeLabel, dateLabel]);

  if (!isTooltipActive || !activeCoordinate || !offset || leadTime == null)
    return null;

  const width = textWidth + PADDING_X * 2;
  const svgWidth = offset.left + offset.width + offset.right;
  // Keep the badge inside the drawing near both edges.
  const centerX = Math.min(
    Math.max(activeCoordinate.x, width / 2),
    svgWidth - width / 2
  );
  const top =
    offset.top + offset.height + offset.bottom - LEAD_TIME_BADGE_HEIGHT;
  // Two hanging-baseline lines centered in the badge.
  const firstLineTop = top + (BADGE_HEIGHT - 2 * LINE_HEIGHT) / 2;

  return (
    <g pointerEvents="none">
      <rect
        x={centerX - width / 2}
        y={top}
        width={width}
        height={BADGE_HEIGHT}
        rx={RADIUS}
        fill={theme.vars.palette.tooltip.background}
      />
      <text
        ref={textRef}
        x={centerX}
        textAnchor="middle"
        fontSize={AXIS_TICK_FONT_SIZE}
        fontWeight={600}
        fill={theme.vars.palette.text.contrastPrimary}
      >
        <tspan x={centerX} y={firstLineTop} dominantBaseline="hanging">
          {leadTimeLabel}
        </tspan>
        <tspan
          x={centerX}
          y={firstLineTop + LINE_HEIGHT}
          dominantBaseline="hanging"
        >
          {dateLabel}
        </tspan>
      </text>
    </g>
  );
};

export default LeadTimeAxisBadge;
